import type { MouseEvent, ReactNode } from "react";

import { Text } from "@ps-design-system/components/Text";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from "@ps-design-system/components/Tooltip";
import QuestionIcon from "@ps-design-system/icons/QuestionFilled/QuestionFilled16";

type FieldLabelWithTooltipProps = {
  /** The tooltip's copy. */
  body: ReactNode;
  /** Bold line above the body, for a tooltip that poses a question first. */
  heading?: ReactNode;
  /** The field label the tooltip hangs off. */
  label: ReactNode;
  /** Accessible name for the tooltip trigger, already translated. */
  triggerLabel: string;
};

/**
 * A field label with a "?" button that explains it. Today that is the country
 * field, locked once an account is pinned to one country.
 */
const FieldLabelWithTooltip = ({
  body,
  heading,
  label,
  triggerLabel,
}: FieldLabelWithTooltipProps) => (
  // Overrides styleguide.less lowercasing every label, and the disabled styling the
  // design system applies to the label of a disabled field.
  <span className="tw-inline-flex tw-items-center tw-gap-4 tw-normal-case tw-text-gray-900">
    {label}
    <Tooltip>
      <TooltipTrigger asChild={true}>
        <button
          aria-label={triggerLabel}
          className="tw-pointer-events-auto tw-inline-flex tw-cursor-pointer tw-items-center tw-text-gray-600"
          // This sits inside the field's `<label>`, so a bare click would fall
          // through and focus the field instead of opening the tooltip.
          onClick={(event: MouseEvent<HTMLButtonElement>) =>
            event.preventDefault()
          }
          type="button"
        >
          <QuestionIcon aria-hidden={true} />
        </button>
      </TooltipTrigger>
      <TooltipContent>
        {heading && (
          <Text element="p" typeface="body-1-m">
            {heading}
          </Text>
        )}
        {body}
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  </span>
);

export { FieldLabelWithTooltip };

export type { FieldLabelWithTooltipProps };
